export type DebouncedFn<Args extends unknown[]> = ((...args: Args) => void) & {
  cancel: () => void;
};
/**
 * Creates a debounced function that delays the invocation of the provided function
 * until after the specified wait time has elapsed since the last time the debounced
 * function was called. This helps in preventing the function from being called
 * multiple times in quick succession.
 *
 * @template Args - The argument types of the function being debounced.
 *
 * @param {(...args: Args) => void} func - The function to debounce.
 * @param {number} wait - The number of milliseconds to delay.
 *
 * @returns {(...args: Args) => void} - A debounced version of the provided function.
 *
 * @example
 * // Example usage:
 * const debouncedFunction = debounce(() => {
 *   console.log('Function executed after 300ms of inactivity');
 * }, 300);
 *
 * // Calling the debounced function multiple times within 300ms will only execute the function once.
 * window.addEventListener('resize', debouncedFunction);
 */
export default function debounce<Args extends unknown[]>(
  func: (...args: Args) => void,
  wait: number,
): DebouncedFn<Args> {
  let timeout: ReturnType<typeof setTimeout> | null = null;
  function debounced(...args: Args) {
    if (timeout) clearTimeout(timeout);

    timeout = setTimeout(() => {
      func(...args);
      timeout = null;
    }, wait);
  }

  debounced.cancel = () => {
    if (timeout) {
      clearTimeout(timeout);
      timeout = null;
    }
  };

  return debounced;
}
